import { arrConcat, arrInc, arrSumF } from '../../../fun-js-lib/arr'
import { canvasFonted, canvasTextWidthFonted } from '../../../fun-js-lib/canvas'
import { breakListIntoLines, indentForNodeMark } from './break-list'
import { breakParagraphIntoLines } from './break-paragraph'
import { isOrderedList, isText, isUnorderedList } from './node-type'
import { spaceWidth } from './space-width'
import { breakChildrenIntoSpans, breakElementIntoSpans, getStyledFont } from './span'

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} json 
 * @param {number[]} linesWidths 
 */
export function getLines (context, json, linesWidths) {
  if (json !== '') { // can be an empty string when daily page has no entries (freshly created)
    const html = JSON.parse(json).html
    const parser = new DOMParser()
    const body = parser.parseFromString(html, "text/html").body
    const lines = breakBodyIntoLines(context, body, linesWidths)
    removeTrailingEmptyLines(lines)
    return lines
  } else {
    return []
  }
}

/**
 * @param {Line[]} lines 
 */
function removeTrailingEmptyLines (lines) {
  while (lines.length > 0 && lines[lines.length - 1].empty) {
    lines.pop()
  }
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} json 
 * @param {number[]} linesWidths 
 * @param {string} font 
 */
export function getLinesFonted (context, json, linesWidths, font) {
  let lines = []
  canvasFonted(context, font, () => {
    lines = getLines(context, json, linesWidths)
  })
  return lines
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {HTMLElement} body
 * @param {number[]} linesWidths 
 */
function breakBodyIntoLines (context, body, linesWidths) {
  const paragraphs = Array.from(body.childNodes)
  return arrConcat(paragraphs.map(p => {
    if (isUnorderedList(p) || isOrderedList(p)) {
      return breakListIntoLines(p, context, linesWidths)
    } else if (isText(p)) { // element can be text node, should be treated as paragraph
      const spans = breakElementIntoSpans(p)
      return breakParagraphIntoLines(context, spans, linesWidths, 0)
    } else {
      const spans = breakChildrenIntoSpans(p)
      return breakParagraphIntoLines(context, spans, linesWidths, 0)
    }
  }))
}

export const indentPerDepth = 50

export class Line {
  /**
   * @param {import('./word-span').default[]} wordSpans 
   */
  constructor (wordSpans) {
    this.wordSpans = wordSpans
    this.isUnorderedList = false
    this.isOrderedList = false
    this.nestingLevel = 0
    this.orderNumber = 0
  }
  get empty () {
    return this.wordSpans.every(wordSpan => wordSpan.text === '')
  }
  get indent () {
    return this.nestingLevel * indentPerDepth +
      (this.isList ? indentForNodeMark : 0)
  }
  get isList () {
    return this.isUnorderedList || this.isOrderedList
  }
  /** Used to output order number */
  get orderNumberString () {
    if (this.nestingLevel % 3 === 0) {
      return '' + this.orderNumber + '.'
    } else if (this.nestingLevel % 3 === 1) {
      const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('')
      return alphabet[this.orderNumber - 1] + '.'
    } else {
      return romanize(this.orderNumber).toLowerCase() + '.'
    }
  }
  /**
   * Returns true iff last word span has space after it
   */
  get hasRightSpace () {
    return this.wordSpans.length === 0 ||
      this.wordSpans[this.wordSpans.length - 1].hasRightSpace
  }
  /** Returns number of spaces between word spans in line */
  get nSpaces () {
    const indexesExceptLast = arrInc(this.wordSpans.length - 1)
    return arrSumF(indexesExceptLast, i => this.hasSpaceAfter(i) ? 1 : 0)
  }
  /**
   * @param {number} i 
   */
  hasSpaceAfter (i) {
    const current = this.wordSpans[i]
    const isLast = i === this.wordSpans.length - 1
    return current.hasRightSpace ||
      (!isLast && this.wordSpans[i + 1].hasLeftSpace)
  }
  /**
   * Width of line considering possible spaces between word spans
   * @param {CanvasRenderingContext2D} context 
   */
  width (context) {
    let w = 0
    for (let i = 0; i < this.wordSpans.length; ++i) {
      const current = this.wordSpans[i]
      w += current.width(context) + (this.hasSpaceAfter(i) ? spaceWidth(context) : 0)
    }
    return w
  }
  /**
   * @param {import('./word-span').default} wordSpan 
   */
  append (wordSpan) {
    this.wordSpans.push(wordSpan)
  }
  /**
   * @param {CanvasRenderingContext2D} context 
   */
  getSumOfWordsWidths (context) {
    return arrSumF(this.wordSpans, span => {
      const font = getStyledFont(span, context.font)
      return canvasTextWidthFonted(context, span.text, font)
    })
  }
}

/**
 * Returns roman number as string from integer `num`. Picked from the web
 * @param {number} num 
 */
function romanize (num) {
  var digits = String(+num).split(""),
    key = ["", "C", "CC", "CCC", "CD", "D", "DC", "DCC", "DCCC", "CM",
      "", "X", "XX", "XXX", "XL", "L", "LX", "LXX", "LXXX", "XC",
      "", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"],
    roman = "",
    i = 3;
  while (i--)
    roman = (key[+digits.pop() + (i * 10)] || "") + roman;
  return Array(+digits.join("") + 1).join("M") + roman;
}